<template>
  <div class="max-w-3xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">{{ placeName ? `${placeName} ${page.title}` : page.title }}</h1>
    <LocationProblem v-if="locationError" :error="locationError" subject="snow totals near you" my-4 />
    <LocationLookup v-model="selectedLocation" />
    <div space-y-4>
      <div v-if="snowForecast">
        <h2 text-2xl font-semibold text-stone-500>48 hour Snowfall Forecast</h2>
        <div text-3xl font-semibold text-stone-700 tabular-nums>{{ snowForecast }}</div>
        <div font-mono text text-stone-500 lh-snug></div>
      </div>

      <h2 text-2xl font-semibold text-stone-500>Snowfall Map</h2>
      <LazyMapSnowfall :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        See how much snow fell {{ around }} over the last 6, 24, 48, and 72 hours, from the National Water Center's
        <a href="https://www.nohrsc.noaa.gov/snowfall_v2/">National Gridded Snowfall Analysis</a>. The analysis combines
        snowfall reports, high-resolution weather models, precipitation estimates, and temperature data. Drag the marker
        to see the snowfall at any other point.
      </p>

      <div flex flex-col md:flex-row gap-4 justify-between pt-6>
        <h2 text-2xl font-semibold text-stone-500>Station Readings</h2>
        <div class="md:(border-0 p-0) border-3 border-stone-100 rounded px-4 py-2 flex gap-4 items-center">
          <label for="date" class="font-semibold text-stone-500 md:sr-only">Date</label>
          <DateSelect id="date" v-model="date" />
        </div>
      </div>

      <div w-full h-lg bg-stone-50>
        <LazyMapSnowReadings :readings="snow?.data || []" :location="coordinates" @markerMove="setMapLocation" />
      </div>

      <div grid grid-flow-dense sm:grid-cols-2 gap-2 relative>
        <template v-for="reading in readings" :key="reading?.station_id">
          <SnowReading :reading="reading" :loading="snow?.pending || loading" />
        </template>
      </div>
      <div v-if="(snow?.data?.length > 4 || loading) && !showAllReadings" class="text-center">
        <Button :disabled="loading" text-lg color="blue" @click="showAllReadings = true">Show more</Button>
      </div>

      <p prose lg:text-lg>
        These are the snow reports nearest {{ shortPlaceName || "this location" }} for the selected date, collected by
        the National Weather Service's
        <a href="https://www.nohrsc.noaa.gov">National Operational Hydrologic Remote Sensing Center</a> from weather
        observers and automated stations. New snowfall and snow depth readings arrive throughout the day, usually within
        24 hours of the observation.
      </p>

      <p v-if="coordinates && readings.length === 0 && !loading">
        No snowfall readings were found for this location and date.
      </p>

      <div space-y-4 v-if="snow && selectedLocation?.module === 'currentLocation'">
        <div text-stone-5 prose lg:text-lg v-if="isLocationSupported">
          <p v-if="!loading && snow?.error && !isBot()">An error occurred. Please try again later.</p>
          <p v-else-if="!loading && !snow?.data && !isBot()">
            We could not determine the {{ page.label }} for your location.
          </p>
        </div>
      </div>

      <h2 text-2xl font-semibold text-stone-500>Current Snow Depth</h2>
      <LazyMapSnowDepth :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        See how much snow is on the ground {{ around }} today, from the daily
        <a href="https://nsidc.org/home">SNODAS</a> snow analysis. Drag the marker to see the snow depth anywhere in the
        United States.<template v-if="coverDays">
          In an average year, {{ shortPlaceName }} has snow on the ground on about {{ coverDays }} days.</template
        >
      </p>

      <h2 text-2xl font-semibold text-stone-500>Seasonal Snowfall Map</h2>
      <LazyMapSnowfallSeason :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        See all the snow that has fallen {{ around }} this season, since September 30. The season total is updated every
        12 hours from the <a href="https://www.nohrsc.noaa.gov/snowfall_v2/">National Gridded Snowfall Analysis</a>.
        Drag the marker to see the season total anywhere in the United States.<template v-if="averageSnowfall">
          A full season in {{ shortPlaceName }} brings about {{ averageSnowfall }} on average.</template
        >
      </p>

      <SnowNormals
        v-if="snowPlace?.normals || snowPlace?.page"
        :normals="snowPlace.normals"
        :name="shortPlaceName"
        :page="snowPlace.page"
        :county="snowPlace.county"
        :state="snowPlace.state"
        :state-share="snowPlace.stateShare"
      />

      <div space-y-4>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>{{ countyType }} Snowfall Records</h2>
        <h3 text-xl font-semibold text-stone-400 v-if="recordsLoading || snowExtremes?.data?.county">
          <template v-if="recordsLoading">&nbsp;</template>
          <template v-else> {{ snowExtremes?.data?.countyName }}, {{ snowExtremes?.data?.state }} </template>
        </h3>
        <div grid sm:grid-cols-3 gap-2>
          <template v-for="reading in snowExtremes?.data?.readings" :key="reading.station_id">
            <SnowReading :reading="reading" />
          </template>
          <template v-if="recordsLoading">
            <SnowReading
              v-for="_ in [].constructor(3)"
              :loading="true"
              :class="{ invisible: snowExtremes?.data?.readings?.length == 0 }"
            />
          </template>
        </div>
        <template v-if="coordinates">
          <p prose lg:text-lg v-if="!recordsLoading && !snowExtremes?.data?.description">
            No snowfall records were found for
            <template v-if="snowExtremes?.data?.county"
              >{{ snowExtremes?.data?.countyName }}, {{ snowExtremes?.data?.state }}</template
            ><template v-else>this {{ countyType.toLowerCase() }}</template
            >.
          </p>
          <p prose lg:text-lg v-else>
            These readings are the most snow ever recorded in one, two, and three days at weather stations in
            <template v-if="snowExtremes?.data?.countyName">{{ snowExtremes.data.countyName }}</template
            ><template v-else>this {{ countyType.toLowerCase() }}</template
            >.
          </p>
        </template>
        <p prose lg:text-lg>{{ snowExtremes?.data?.description }}</p>
      </div>

      <SnowNearby
        v-if="snowPlace?.countyPlaces?.length"
        :heading="`Snow Totals for Towns in ${snowPlace.page.name} ${countyType}`"
        :places="snowPlace.countyPlaces"
        :state="snowPlace.state"
      />
      <SnowNearby
        v-else-if="snowPlace?.nearby?.length || snowPlace?.county"
        :heading="`Snow Totals Near ${shortPlaceName || 'This Location'}`"
        :places="snowPlace.nearby"
        :county="snowPlace.county"
        :state="snowPlace.state"
      />

      <Ad ad-slot="1948746426" />
      <WikipediaInfo v-if="placeName" :slug="placeName" />
    </div>

    <div class="prose lg:text-lg">
      <h2 class="text-3xl font-semibold text-stone-500">{{ page?.subtitle || "What is this?" }}</h2>
      <ContentRenderer v-if="page" :value="page" />
    </div>
  </div>
</template>
<script setup>
const page = await useContent("/snow");
const route = useRoute();

const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange, geolocation } =
  useSelectedLocation();
const { isLocationSupported } = geolocation;

// Facts about the location that don't change (average snowfall, records, nearby towns), rendered on the server so
// each town's page says something of its own. Refetched when the location changes.
const placeQuery = computed(() => coordinates.value && { ...coordinates.value, title: placeName.value });
// awaited at the end of setup, after the head tags that depend on it are registered
const snowPlaceRequest = useAsyncData(
  "snow-place",
  () => (placeQuery.value ? fetchSnowPlace(placeQuery.value, placeQuery.value.title) : Promise.resolve(null)),
  { watch: [placeQuery] },
);
const { data: snowPlace } = snowPlaceRequest;

// "Buffalo" for "Buffalo, New York"
const shortPlaceName = computed(() => snowPlace.value?.page?.name ?? placeName.value?.split(",")[0] ?? null);

const averageSnowfall = computed(() => {
  const annual = snowPlace.value?.normals?.annual;
  return isNumber(annual) && annual > 0 ? `${numberFormatted(annual, { decimals: 0, avoidZero: true })} inches` : null;
});

// "around Buffalo", or "around this location" for an unnamed point
const around = computed(() => `around ${shortPlaceName.value || "this location"}`);

const coverDays = computed(() => {
  const days = snowPlace.value?.normals?.coverDays;
  return shortPlaceName.value && isNumber(days) && days >= 1 ? Math.round(days) : null;
});

// Location URLs from the atlas are the pages that rank, so name the place in the title,
// heading, and description.
const description = computed(() => {
  if (placeName.value) {
    const average = averageSnowfall.value ? ` ${shortPlaceName.value} averages ${averageSnowfall.value} a year.` : "";
    return `How much snow did ${placeName.value} get? See snowfall for the last 24, 48, and 72 hours, nearby station reports, snow depth, and the season total.${average}`;
  }
  return page.value.description;
});

// One URL per town: the one the atlas, county pages, and sitemap link to. Other searched locations get no canonical.
const canonicalUrl = computed(() => {
  if (snowPlace.value?.page) {
    return `${SITE_URL}${snowPlace.value.page.url}`;
  }
  return route.query.lat == null ? `${SITE_URL}/snow-totals` : null;
});

// Snow Totals > State > County > town, for search results
const breadcrumbs = computed(() => {
  const place = snowPlace.value;
  if (!place?.page) {
    return null;
  }
  const items = [
    { name: "Snow Totals", item: `${SITE_URL}/atlas/snow-totals` },
    { name: place.state.name, item: `${SITE_URL}${place.state.atlasUrl}` },
  ];
  if (place.county) {
    items.push({ name: place.county.fullName, item: `${SITE_URL}${place.county.url}` });
  }
  items.push({ name: place.page.title, item: `${SITE_URL}${place.page.url}` });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({ "@type": "ListItem", position: i + 1, ...item })),
  };
});

useHead({
  link: computed(() => (canonicalUrl.value ? [{ rel: "canonical", href: canonicalUrl.value }] : [])),
  script: computed(() =>
    breadcrumbs.value
      ? [{ type: "application/ld+json", key: "breadcrumbs", innerHTML: JSON.stringify(breadcrumbs.value) }]
      : [],
  ),
});

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} ${page.value.title} - Recent Snowfall`;
    }
    return `${page.value.title} - ${page.value.subtitle}`;
  },
  description,
  ogDescription: description,
});

defineOgImage({
  title: page.value.title,
  subtitle: page.value.subtitle,
});

await snowPlaceRequest;

const date = ref(toISODate(new Date()));
const snow = ref();
// the records come with the place on the server; afterwards they are fetched for each new location
const snowExtremes = ref(
  snowPlace.value?.records ? { data: snowPlace.value.records, pending: false, location: coordinates.value } : undefined,
);

function sameLocation(a, b) {
  return a?.latitude === b?.latitude && a?.longitude === b?.longitude;
}
const recordsLoading = computed(() => !snowExtremes.value?.data && (loading.value || snowExtremes.value?.pending));
// "County" unless the records are for e.g. a Louisiana parish or an Alaska borough
const countyType = computed(() => snowExtremes.value?.data?.countyType || "County");
const forecast = ref();
const showAllReadings = ref(false);

const readings = computed(() => {
  if (snow.value?.pending === true || loading.value) {
    return [].constructor(4);
  }
  if (!snow.value?.data?.length) {
    return [];
  }
  const readingsWithData = snow.value.data.filter(
    (reading) => isNumber(reading?.snowfall_inches) || isNumber(reading?.snow_depth_inches),
  );
  return showAllReadings.value ? readingsWithData : readingsWithData.slice(0, 4);
});

const loading = ref(true);

async function update() {
  if (!coordinates.value) {
    return;
  }
  const keepRecords = snowExtremes.value?.data && sameLocation(snowExtremes.value.location, coordinates.value);
  loading.value = true;
  snow.value = null;
  if (!keepRecords) {
    snowExtremes.value = null;
  }
  forecast.value = null;
  try {
    // the date is a little messed up with timezones, and sometimes setting today doesn't work
    // for now, just don't send the date if it's today
    const requestDate = date.value == toISODate(new Date()) ? null : date.value;
    snow.value = await useFetchSnow(coordinates.value, requestDate);
    if (!keepRecords) {
      const location = coordinates.value;
      snowExtremes.value = await useFetchSnowExtremes(location);
      snowExtremes.value.location = location;
    }
    if (!date.value) {
      // TODO: reenable once UI is straightened out
      // only fetch forecast if date is not set (default to current day)
      // forecast.value = await useFetchForecast(coordinates.value, "snow");
    }
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
}

onCoordinatesChange(update);
// there is nothing to load until a location is chosen
watch(locationError, (error) => {
  if (error) {
    loading.value = false;
  }
});
watch(date, update);

const snowForecast = computed(() => {
  const forecastData = forecast.value?.data;
  if (!isNumber(forecastData?.low48) || !isNumber(forecastData?.high48)) {
    return null;
  }
  if (forecastData.low48 == forecastData.high48) {
    return `${forecastData.low48}\u00A0in`;
  }
  return `${forecastData.low48}–${forecastData.high48}\u00A0in`;
});
</script>
